import React, { Component, createRef } from "react";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

import "./rich-text-editor.css";

/**
 * Reusable rich-text editor. Drop-in: just pass `value` (HTML string),
 * `onChange(html)`, and optionally `onImageUpload(file) => Promise<url>` to
 * enable the toolbar's image button.
 *
 * Why custom image handler: Quill's default base64-encodes images into the
 * document — fine for the editor preview but horrible to store/transmit, and
 * impossible to highlight or cite later. With `onImageUpload`, we instead
 * upload the file to S3 and insert an <img src="https://…"> tag pointing at
 * the real asset.
 *
 * Props:
 *   value            : string  — HTML content (controlled)
 *   onChange         : (html) => void
 *   onImageUpload    : (File) => Promise<string>  — must resolve to a URL
 *   onImageUploaded  : (img) => void              — fires after a successful
 *                       upload with { url, key } so the parent can persist a
 *                       canonical image list separately from the HTML
 *   placeholder      : string
 *   minHeight        : number  (default 220)
 *   readOnly         : boolean
 *   toolbar          : Quill toolbar config (defaults to a sensible set)
 */
class RichTextEditor extends Component {
    constructor(props) {
        super(props);
        this._quillRef = createRef();
        this._fileInputRef = createRef();
    }

    componentDidMount() {
        if (this._fileInputRef.current) {
            this._fileInputRef.current.addEventListener("change", this._onFileSelected);
        }
    }

    componentWillUnmount() {
        if (this._fileInputRef.current) {
            this._fileInputRef.current.removeEventListener("change", this._onFileSelected);
        }
    }

    /**
     * Quill toolbar handler — replaces the default base64 inserter with our
     * S3-upload-then-insert flow.
     */
    _imageHandler = () => {
        if (!this.props.onImageUpload) {
            // Caller didn't opt in to uploads — silently fall back to the
            // default behavior (which embeds base64). Better than nothing.
            return;
        }
        if (this._fileInputRef.current) {
            this._fileInputRef.current.value = ""; // allow re-picking the same file
            this._fileInputRef.current.click();
        }
    };

    _onFileSelected = async () => {
        const input = this._fileInputRef.current;
        if (!input || !input.files || !input.files[0]) return;
        const file = input.files[0];

        const quill = this._quillRef.current && this._quillRef.current.getEditor();
        if (!quill) return;

        const range = quill.getSelection(true);
        // Insert a placeholder while uploading so the cursor doesn't jump on success
        const placeholder = "[Uploading image…]";
        quill.insertText(range.index, placeholder, "italic", true);

        try {
            const url = await this.props.onImageUpload(file);
            // Replace the placeholder text with an actual image
            quill.deleteText(range.index, placeholder.length);
            quill.insertEmbed(range.index, "image", url);
            quill.setSelection(range.index + 1);

            if (this.props.onImageUploaded) {
                this.props.onImageUploaded({ url, name: file.name });
            }
        } catch (err) {
            quill.deleteText(range.index, placeholder.length);
            // Surface the error to the user via a brief inline note. The host
            // component can also wire `onError` if it wants a toast.
            quill.insertText(range.index, "[Image upload failed]", { color: "#c00" });
            if (this.props.onError) this.props.onError(err);
        }
    };

    _modules = () => {
        const toolbar = this.props.toolbar || [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["blockquote", "code-block"],
            ["link", "image"],
            ["clean"],
        ];
        return {
            toolbar: {
                container: toolbar,
                handlers: { image: this._imageHandler },
            },
            clipboard: { matchVisual: false },
        };
    };

    render() {
        const {
            value = "",
            onChange,
            placeholder = "Start typing…",
            minHeight = 220,
            readOnly = false,
        } = this.props;

        return (
            <div className="rte" style={{ "--rte-min-height": minHeight + "px" }}>
                <ReactQuill
                    ref={this._quillRef}
                    theme="snow"
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    readOnly={readOnly}
                    modules={this._modules()}
                />
                {/* Hidden file picker driven by the toolbar's image handler */}
                <input
                    ref={this._fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    style={{ display: "none" }}
                />
            </div>
        );
    }
}

export { Quill };
export default RichTextEditor;
